import React, { useState, useEffect } from "react";
import { TextField, MenuItem, Button, Typography, Snackbar, Grid, CircularProgress, Card, CardContent, Box } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { ArrowBack, Save } from "@material-ui/icons";
import { API_POST } from "../../services/api.service";
import { findChanges, mergeHistoryEntries, TICKET_HISTORY_CONFIG } from "./ticketHistoryConfig";
import TicketHistoryComponent from "./TicketHistoryComponent";

const EditTicketPage = ({ onBack, onSuccess, user, configData, teamMembers, editData }) => {
  const [formData, setFormData] = useState(editData || {});
  const [originalData, setOriginalData] = useState(editData || {});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', color: 'success' });
  const [fields, setFields] = useState([]);

  useEffect(() => {
    setFormData(editData || {});
    setOriginalData(editData || {});
  }, [editData]);

  useEffect(() => {
    generateFields();
  }, [configData, teamMembers]);

  const generateFields = () => {
    const baseFields = [
      {
        key: "priority",
        label: "Priority",
        type: "select",
        options: [
          "Minus P1",
          "P0",
          "P1",
          "P2",
          "P3"
        ]
      },
      {
        key: "developmentStatus",
        label: "Development Status",
        type: "select",
        options: [
          "In Progress",
          "In Roadmap",
          "Under Development",
          "QA Tested",
          "Move For Deployment",
          "Not Found",
          "Under QA Testing",
          "Resolved",
          "Reopen"
        ]
      },
      { key: "assignTo", label: "Assign To", type: "select", options: teamMembers },
      { key: "asanaLink", label: "Asana Link", type: "text" },
      { key: "estimationTime", label: "Estimation Time", type: "text" },
      { key: "deployedDate", label: "Deployed Date", type: "date" },
      { key: "description", label: "Comment", type: "textarea" , required: true },
      { key: "comment", label: "Comment", type: "textarea" , required: false,placeHolder: "Comment...." },

    ];

    // Add dynamic fields based on configuration
    const dynamicFields = [...baseFields];

    // Add type field if configured
    if (configData.types && configData.types.length > 0) {
      dynamicFields.splice(1, 0, {
        key: "type",
        label: "Type",
        type: "select",
        options: configData.types
      });
    }

    // Add module field if configured
    if (configData.modules && configData.modules.length > 0) {
      dynamicFields.splice(2, 0, {
        key: "module",
        label: "Module",
        type: "select",
        options: configData.modules
      });
    }

    // Add category field if configured
    if (configData.categories && configData.categories.length > 0) {
      dynamicFields.splice(3, 0, {
        key: "category",
        label: "Category",
        type: "select",
        options: configData.categories
      });
    }

    // Add subcategory field if configured
    if (configData.subcategories && configData.subcategories.length > 0) {
      dynamicFields.splice(4, 0, {
        key: "subCategory",
        label: "SubCategory",
        type: "select",
        options: configData.subcategories
      });
    }

    setFields(dynamicFields);
  };

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const currentTime = new Date().toISOString();
      
      // Find changes between original and new data
      const changes = findChanges(originalData, formData, user);
      
      // Merge with existing history
      const existingHistory = originalData.history || [];
      const updatedHistory = mergeHistoryEntries(existingHistory, changes);
      
      const dataToSave = {
        ...formData,
        admin: user.email || '',
        username: user.firstName || user.username || '',
        updatedAt: currentTime,
        history: updatedHistory
      };

      await API_POST("action/update", {
        root: "supportDevTickets",
        querydata: { _id: editData._id },
        body: dataToSave,
      });

      for (let i in formData['mappedId'] ){
        await API_POST("action/update", {
          root: "supportTickets",
          querydata: { _id: formData['mappedId'][i] },
          body: {developmentStatus:formData.developmentStatus,devTicketAssigned:formData.assignTo },
        });
      }

      

      setSnackbar({ open: true, message: 'Ticket updated successfully', color: 'success' });
      
      // Update original data to reflect the saved state
      setOriginalData(dataToSave);
      
      // Call success callback after a short delay
      setTimeout(() => {
        if (onSuccess) {
          // onSuccess();
        }
      }, 1500);

    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error updating ticket', color: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    if (field.key === "subCategory") {
      return (
        <Grid item xs={12} sm={6} md={4} key={field.key}>
          <Autocomplete
            options={field.options || []}
            value={formData.subCategory || null}
            onChange={(event, newValue) => handleFieldChange("subCategory", newValue || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                variant="outlined"
                required={field.required}
              />
            )}
            freeSolo
            autoComplete
            includeInputInList
            filterSelectedOptions
          />
        </Grid>
      );
    }
    if (field.key === "category") {
      return (
        <Grid item xs={12} sm={6} md={4} key={field.key}>
          <Autocomplete
            options={field.options || []}
            value={formData.category || null}
            onChange={(event, newValue) => handleFieldChange("category", newValue || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                variant="outlined"
                required={field.required}
              />
            )}
            freeSolo
            autoComplete
            includeInputInList
            filterSelectedOptions
          />
        </Grid>
      );
    }
    if (field.type === "select") {
      return (
        <Grid item xs={12} sm={6} md={4} key={field.key}>
          <TextField
            select
            label={field.label}
            value={formData[field.key] || ""}
            onChange={e => handleFieldChange(field.key, e.target.value)}
            fullWidth
            required={field.required}
            variant="outlined"
          >
            {field.options.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }
    if (field.key === "description" || field.key === "comment") {
      return (
        <Grid item xs={12} key={field.key}>
          <TextField
            value={formData[field.key] || ""}
            onChange={e => handleFieldChange(field.key, e.target.value)}
            fullWidth
            required={field.required}
            placeholder={field.placeHolder || field.key}
            variant="outlined"
            multiline
            rows={4}
          />
        </Grid>
      );
    }
    if (field.key === "createdAt" || field.key === "updatedAt") {
      const date = formData[field.key];
      const formattedDate = date ? new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString() : '';
      return (
        <Grid item xs={12} sm={6} md={4} key={field.key}>
          <TextField
            label={field.label}
            value={formattedDate}
            fullWidth
            variant="outlined"
            disabled={true}
            InputProps={{
              readOnly: true
            }}
          />
        </Grid>
      );
    }
    return (
      <Grid item xs={12} sm={6} md={4} key={field.key}>
        <TextField
          label={field.label}
          type={field.type}
          value={formData[field.key] || ""}
          onChange={e => handleFieldChange(field.key, e.target.value)}
          fullWidth
          required={field.required}
          variant="outlined"
          disabled={field.readonly}
          InputProps={{
            readOnly: field.readonly
          }}
        />
      </Grid>
    );
  };

  if (!editData) {
    return (
      <div style={{ margin: '0 auto', padding: 24 }}>
        <Box display="flex" alignItems="center" marginBottom={3}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={onBack}
            style={{ marginRight: 16 }}
          >
            Back
          </Button>
          <Typography variant="h4" style={{ fontWeight: 600, color: '#1976d2' }}>
            Edit Support Dev Ticket
          </Typography>
        </Box>
        <Typography variant="body1" color="error">
          No ticket data provided for editing.
        </Typography>
      </div>
    );
  }

  return (
    <div style={{ margin: '0 auto', padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={onBack}
          style={{ marginRight: 16, fontWeight: 500 }}
        >
          Back to Tickets
        </Button>
        <Typography variant="h4" style={{ fontWeight: 600, color: '#1976d2' }}>
          Edit Support Dev Ticket
        </Typography>
      </div>

      <Grid container spacing={3}>
        {/* Left Side - Form */}
        <Grid item xs={12} md={6}>
          <Card style={{ boxShadow: '0 2px 8px rgba(33, 150, 243, 0.07)' }}>
            <CardContent>
              <Typography variant="h6" style={{ marginBottom: 24, fontWeight: 600, color: '#1976d2' }}>
                Ticket Details
              </Typography>
              
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {fields.map(field => renderField(field))}
                </Grid>
                <div style={{ marginTop: 32, textAlign: 'center' }}>
                  <Button 
                    type="submit" 
                    color="primary" 
                    variant="contained" 
                    disabled={loading} 
                    startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                    style={{ minWidth: 140, fontWeight: 500 }}
                  >
                    {loading ? "Updating..." : "Update Ticket"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side - History */}
        <Grid item xs={12} md={6}>
          <TicketHistoryComponent 
            history={formData.history || []} 
            ticketId={editData._id} 
          />
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        ContentProps={{ 
          style: { 
            backgroundColor: snackbar.color === 'success' ? '#43a047' : '#d32f2f', 
            color: '#fff' 
          } 
        }}
      />
    </div>
  );
};

export default EditTicketPage; 